import { apiRequest } from '@/api/http';
import {
  AdminLoginResponse,
  EnrollResponse,
  ConfirmEnrollResponse,
  CancelEnrollResponse,
  TokenPairResponse,
  MeResponse,
} from '@/api/schemas/auth';

export function adminLogin(body: { email: string; password: string }) {
  return apiRequest<AdminLoginResponse>('/auth/admin-login', {
    method: 'POST',
    body,
    auth: false,
    schema: AdminLoginResponse,
  });
}

export function enrollFirstTime(mfaChallengeToken: string) {
  return apiRequest<EnrollResponse>('/auth/admin-login/enroll-first-time', {
    method: 'POST',
    body: { mfaChallengeToken },
    auth: false,
    schema: EnrollResponse,
    // The challenge token is consumed on sight; see `verifyMfa`.
    oneTimeSecret: true,
  });
}

// Re-enrolamiento MFA de un admin YA autenticado (#86). No lleva body: el
// backend toma el usuario del Bearer y exige sesión con MFA verificado
// (`mfa: true` en el access token).
//
// #429 — Esto es la FASE 1 y ya NO reemplaza nada: emite un secreto PENDIENTE
// y deja el dispositivo activo intacto. El cambio solo se consuma en
// `confirmMfaEnroll`, así que abandonar aquí no tiene consecuencias.
export function enrollMfa() {
  return apiRequest<EnrollResponse>('/auth/admin-login/enroll', {
    method: 'POST',
    schema: EnrollResponse,
  });
}

/**
 * #429 — Fase 2: el servidor valida el código contra el secreto pendiente y solo
 * entonces promueve el dispositivo nuevo. Hasta que esto responde OK, el
 * autenticador anterior sigue siendo el válido.
 *
 * Sustituye a la verificación local que se hacía antes en el navegador: aquella
 * comprobaba que la app autenticadora funcionara, pero no cerraba la ventana en
 * la que el admin podía quedarse sin dispositivo.
 */
export function confirmMfaEnroll(code: string) {
  return apiRequest<ConfirmEnrollResponse>('/auth/admin-login/enroll/confirm', {
    method: 'POST',
    body: { code },
    schema: ConfirmEnrollResponse,
    // The TOTP digits are spent the moment the server reads them, and this is
    // an AUTHENTICATED route that answers 401 when they are wrong. Without this
    // flag the interceptor read that 401 as an expired session, refreshed,
    // replayed the consumed code, got the inevitable second 401 and sent the
    // admin to the login screen — two of their ten attempts per five minutes
    // gone, for a typo.
    oneTimeSecret: true,
  });
}

/** #429 — abandona el re-enrolamiento en curso; el dispositivo activo no se toca. */
export function cancelMfaEnroll() {
  return apiRequest<CancelEnrollResponse>('/auth/admin-login/enroll/cancel', {
    method: 'POST',
    schema: CancelEnrollResponse,
  });
}

export function verifyMfa(body: { mfaChallengeToken: string; code: string }) {
  return apiRequest<TokenPairResponse>('/auth/admin-login/mfa', {
    method: 'POST',
    body,
    auth: false,
    schema: TokenPairResponse,
    // `auth: false` already keeps the interceptor away, so this is belt and
    // braces — but the challenge token AND the code are both single-use, and
    // the reason not to replay them should not depend on a second flag that
    // someone could reasonably flip.
    oneTimeSecret: true,
  });
}

export function refreshSessionRequest(refreshToken: string) {
  return apiRequest<TokenPairResponse>('/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    auth: false,
    schema: TokenPairResponse,
    // Refresh tokens rotate: the one being sent stops being valid the instant
    // the server accepts it. Replaying it would present a revoked token and
    // could look like theft to a backend that watches for reuse.
    oneTimeSecret: true,
  });
}

export function logoutRequest() {
  return apiRequest<void>('/auth/logout', { method: 'POST' });
}

// Identidad real del admin autenticado. Requiere Bearer (auth por defecto).
export function getMe() {
  return apiRequest<MeResponse>('/auth/me', { schema: MeResponse });
}
